import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function LinearEquationSolver() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Linear Equation Solver", url: "https://calclab.app/calculator/linear-equation" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Linear Equation Solver",
      "Solve linear equations Ax + B = C step by step. Free online algebra calculator.",
      "https://calclab.app/calculator/linear-equation",
      lang
    )
  ]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [c, setC] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const solve = () => {
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    const cNum = parseFloat(c);

    if (isNaN(aNum) || isNaN(bNum) || isNaN(cNum)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    if (aNum === 0) {
      toast({
        title: "Invalid Equation",
        description: "Coefficient A cannot be zero in a linear equation.",
        variant: "destructive",
      });
      return;
    }

    // Ax + B = C
    // Ax = C - B
    // x = (C - B) / A
    const x = (cNum - bNum) / aNum;
    setResult(x);

    toast({
      title: "Solution Found",
      description: `x = ${x.toFixed(4)}`,
    });
  };

  const reset = () => {
    setA("");
    setB("");
    setC("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Linear Equation Solver" 
      calculatorPath="/calculator/linear-equation"
      category="math"
      subcategory="Algebra"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Linear Equation Solver</CardTitle>
            <CardDescription>
              Solve equations of the form Ax + B = C
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="a">Coefficient A</Label>
                <Input
                  id="a"
                  type="number"
                  step="any"
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  placeholder="Coefficient of x"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="b">Constant B</Label>
                <Input
                  id="b"
                  type="number"
                  step="any"
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  placeholder="Constant on left side"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="c">Constant C</Label>
                <Input
                  id="c"
                  type="number"
                  step="any"
                  value={c}
                  onChange={(e) => setC(e.target.value)}
                  placeholder="Constant on right side"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={solve} className="flex-1">Solve</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Solution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere">x = {result.toFixed(4)}</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Verify: {a}({result.toFixed(2)}) + {b} = {c}
                      </p>
                    </div>
                    <CopyResultButton value={result.toFixed(4)} />
                  </div>
                </CardContent>
              </Card>

              <CalculatorSuperCalcCTA config={calculatorCTAs["linear-equation"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <MathFormula block>{String.raw`Ax + B = C \Rightarrow x = \frac{C - B}{A}`}</MathFormula>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
